import React, { Component } from 'react';
import { captureReactError, reportFeedback } from '../utils/errorTracker';

/**
 * Global Error Boundary
 * React hatalarını yakalar ve error tracker'a raporlar.
 */
class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
            showFeedback: false,
            feedbackText: ''
        };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        this.setState({ errorInfo });

        // Error tracker'a raporla
        captureReactError(error, errorInfo);

        // Konsola da yaz
        console.error('ErrorBoundary caught an error:', error, errorInfo);
    }

    handleReload = () => {
        window.location.reload();
    };

    handleGoHome = () => {
        window.location.href = '/';
    };

    handleToggleFeedback = () => {
        this.setState(prev => ({ showFeedback: !prev.showFeedback }));
    };

    handleFeedbackChange = (e) => {
        this.setState({ feedbackText: e.target.value });
    };

    handleSubmitFeedback = () => {
        if (this.state.feedbackText.trim()) {
            reportFeedback(this.state.feedbackText, 'crash_report');
            this.setState({
                showFeedback: false,
                feedbackText: '',
                feedbackSent: true
            });
        }
    };

    render() {
        if (this.state.hasError) {
            // Custom fallback UI
            if (this.props.fallback) {
                return this.props.fallback(this.state.error, this.handleReload);
            }

            return (
                <div style={styles.container}>
                    <div style={styles.card}>
                        <div style={styles.icon}>⚠️</div>
                        <h1 style={styles.title}>Bir Şeyler Ters Gitti</h1>
                        <p style={styles.message}>
                            Beklenmeyen bir hata oluştu. Bu hata otomatik olarak kaydedildi ve en kısa sürede düzeltilecektir.
                        </p>

                        {this.state.feedbackSent && (
                            <div style={styles.successMessage}>
                                ✓ Geri bildiriminiz için teşekkürler!
                            </div>
                        )}

                        <div style={styles.actions}>
                            <button style={styles.primaryButton} onClick={this.handleReload}>
                                🔄 Sayfayı Yenile
                            </button>
                            <button style={styles.secondaryButton} onClick={this.handleGoHome}>
                                🏠 Ana Sayfaya Git
                            </button>
                            <button style={styles.feedbackButton} onClick={this.handleToggleFeedback}>
                                💬 Geri Bildirim Ver
                            </button>
                        </div>

                        {this.state.showFeedback && (
                            <div style={styles.feedbackSection}>
                                <textarea
                                    style={styles.textarea}
                                    placeholder="Ne yapmaya çalışıyordunuz? Bize yardımcı olun..."
                                    value={this.state.feedbackText}
                                    onChange={this.handleFeedbackChange}
                                    rows={3}
                                />
                                <button
                                    style={styles.submitButton}
                                    onClick={this.handleSubmitFeedback}
                                    disabled={!this.state.feedbackText.trim()}
                                >
                                    Gönder
                                </button>
                            </div>
                        )}

                        {process.env.NODE_ENV === 'development' && this.state.error && (
                            <details style={styles.details}>
                                <summary style={styles.summary}>Hata Detayları (Geliştirici)</summary>
                                <pre style={styles.errorStack}>
                                    {this.state.error.toString()}
                                    {'\n\n'}
                                    {this.state.error.stack}
                                </pre>
                                {this.state.errorInfo?.componentStack && (
                                    <pre style={styles.componentStack}>
                                        Component Stack:
                                        {this.state.errorInfo.componentStack}
                                    </pre>
                                )}
                            </details>
                        )}
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

const styles = {
    container: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: '#0a0f1a',
        padding: '20px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    },
    card: {
        backgroundColor: '#1a1f2e',
        borderRadius: '12px',
        padding: '40px',
        maxWidth: '500px',
        width: '100%',
        textAlign: 'center',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
        border: '1px solid #2a2f3e'
    },
    icon: {
        fontSize: '64px',
        marginBottom: '20px'
    },
    title: {
        color: '#fff',
        fontSize: '24px',
        marginBottom: '15px',
        fontWeight: '600'
    },
    message: {
        color: '#94a3b8',
        fontSize: '16px',
        marginBottom: '25px',
        lineHeight: '1.5'
    },
    successMessage: {
        backgroundColor: 'rgba(34, 197, 94, 0.1)',
        border: '1px solid rgba(34, 197, 94, 0.3)',
        color: '#22c55e',
        padding: '10px 15px',
        borderRadius: '8px',
        marginBottom: '20px'
    },
    actions: {
        display: 'flex',
        flexDirection: 'column',
        gap: '10px'
    },
    primaryButton: {
        backgroundColor: '#8b5cf6',
        color: '#fff',
        border: 'none',
        padding: '12px 24px',
        borderRadius: '8px',
        fontSize: '16px',
        fontWeight: '500',
        cursor: 'pointer',
        transition: 'background-color 0.2s'
    },
    secondaryButton: {
        backgroundColor: 'transparent',
        color: '#8b5cf6',
        border: '1px solid #8b5cf6',
        padding: '12px 24px',
        borderRadius: '8px',
        fontSize: '16px',
        fontWeight: '500',
        cursor: 'pointer',
        transition: 'all 0.2s'
    },
    feedbackButton: {
        backgroundColor: 'transparent',
        color: '#64748b',
        border: '1px solid #3a4050',
        padding: '10px 20px',
        borderRadius: '8px',
        fontSize: '14px',
        cursor: 'pointer',
        marginTop: '10px'
    },
    feedbackSection: {
        marginTop: '20px',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px'
    },
    textarea: {
        backgroundColor: '#0a0f1a',
        color: '#fff',
        border: '1px solid #3a4050',
        borderRadius: '8px',
        padding: '12px',
        fontSize: '14px',
        resize: 'vertical',
        minHeight: '80px'
    },
    submitButton: {
        backgroundColor: '#22c55e',
        color: '#fff',
        border: 'none',
        padding: '10px 20px',
        borderRadius: '8px',
        fontSize: '14px',
        cursor: 'pointer'
    },
    details: {
        marginTop: '25px',
        textAlign: 'left'
    },
    summary: {
        color: '#64748b',
        cursor: 'pointer',
        padding: '10px',
        borderRadius: '4px',
        backgroundColor: '#0a0f1a'
    },
    errorStack: {
        backgroundColor: '#0a0f1a',
        color: '#ef4444',
        padding: '15px',
        borderRadius: '8px',
        fontSize: '12px',
        overflow: 'auto',
        maxHeight: '200px',
        textAlign: 'left',
        marginTop: '10px'
    },
    componentStack: {
        backgroundColor: '#0a0f1a',
        color: '#f59e0b',
        padding: '15px',
        borderRadius: '8px',
        fontSize: '12px',
        overflow: 'auto',
        maxHeight: '150px',
        textAlign: 'left',
        marginTop: '10px'
    }
};

export default ErrorBoundary;
